/**
 * FeelingPicker - "How did it feel? (optional)" row on the Add / Edit expense sheets.
 *
 * Five emoji circles; tap one to tag the expense, tap it again to clear. The ⓘ
 * opens a small bubble explaining that tags feed the month-end review. With
 * autoTip the bubble opens by itself (used once, the first time someone adds an
 * expense after this update); onTipShown lets the parent remember that.
 */
import { useState, useEffect, useRef } from "react";
import { FEELINGS, getReviewPalette } from "../../utils/monthEndReview";
import { usePlan } from "../../utils/plan"; // plan_08

const SANS = "'DM Sans', sans-serif";

const FeelingPicker = ({ value, onChange, theme = 'pink', autoTip = false, onTipShown }) => {
  const plan = usePlan();
  const P = getReviewPalette(theme);
  const [tip, setTip] = useState(autoTip);
  const [caretX, setCaretX] = useState(150);
  const told = useRef(false);
  const infoRef = useRef(null);

  // The auto bubble only ever shows once: tell the parent as soon as it appears.
  useEffect(() => {
    if (autoTip && !told.current) {
      told.current = true;
      setTip(true);
      if (onTipShown) onTipShown();
    }
  }, [autoTip, onTipShown]);

  // Point the bubble's little arrow at the ⓘ, wherever the label text ends.
  useEffect(() => {
    if (tip && infoRef.current) {
      const b = infoRef.current;
      setCaretX(b.offsetLeft + b.offsetWidth / 2 - 6);
    }
  }, [tip]);

  const pick = (id) => {
    setTip(false);
    if (onChange) onChange(value === id ? '' : id);
  };

  return (
    <div style={{ position: 'relative', fontFamily: SANS }}>
      {tip && (
        <button
          type="button"
          onClick={() => setTip(false)}
          style={{
            position: 'absolute', left: 0, right: 0, bottom: '100%', marginBottom: 2, zIndex: 30,
            display: 'block', textAlign: 'left', padding: '12px 14px', border: 0, borderRadius: 16,
            background: '#FFFFFF', boxShadow: '0 10px 30px rgba(28,27,31,0.16), 0 2px 6px rgba(28,27,31,0.06)',
            fontFamily: SANS, fontSize: 13.5, lineHeight: 1.45, color: '#1F2937',
          }}
        >
          <span style={{ display: 'block', fontWeight: 700, marginBottom: 3 }}>{'\u{1F4C5}'} For your month-end review</span>
          Tag how your spending felt and on the 1st you&rsquo;ll see how much went to treats, impulses and regrets.
          <span style={{ position: 'absolute', left: caretX, bottom: -6, width: 12, height: 12, background: '#FFFFFF', transform: 'rotate(45deg)', boxShadow: '3px 3px 4px rgba(28,27,31,0.05)' }} />
        </button>
      )}

      <div className="flex items-center gap-1.5 mb-1.5 ml-1">
        <p className="text-xs font-medium text-gray-400 m-0" style={{ fontFamily: SANS }}>
          How did it feel? <span className="text-gray-300">(optional)</span>
        </p>
        <button
          ref={infoRef}
          type="button"
          aria-label="What is this for?"
          aria-expanded={tip}
          onClick={() => setTip((t) => !t)}
          style={{ width: 22, height: 22, margin: '-3px 0', padding: 0, border: 0, borderRadius: '50%', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <span style={{ width: 16, height: 16, borderRadius: '50%', background: 'rgba(0,0,0,0.06)', fontSize: 10, fontWeight: 700, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: SANS }}>i</span>
        </button>
      </div>

      <div role="group" aria-label="How did it feel?" style={{ display: 'flex', justifyContent: 'space-between', padding: '0 2px' }}>
        {FEELINGS.map((f) => {
          const on = value === f.id;
          const dim = !!value && !on;
          return (
            <button
              key={f.id}
              type="button"
              aria-pressed={on}
              onClick={() => pick(f.id)}
              style={{ width: 62, padding: 0, border: 0, background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, fontFamily: SANS }}
            >
              <span
                style={{
                  width: 46, height: 46, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 21,
                  background: on ? (plan ? '#ECE6FA' : P.income.fill) : (plan ? '#F7F5FB' : '#F6F4F7'),
                  boxShadow: on ? `inset 0 0 0 2px ${plan ? '#B9A6F0' : P.ui.button}` : 'none',
                  opacity: dim ? 0.55 : 1, transition: 'all 0.15s ease',
                }}
              >
                {f.emoji}
              </span>
              <span style={{ fontSize: 11.5, whiteSpace: 'nowrap', fontWeight: on ? 600 : 400, color: on ? (plan ? '#7A5DD3' : '#1F2937') : (plan ? '#5E5A6B' : '#6b7280') }}>{f.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FeelingPicker;
